/**
 * 資料セクション レジストリ — 全 13 type × 2-4 variant を集約。
 * 公開: getSection / getVariant / addSectionDefaults
 *      USE_CASE_TEMPLATES — 用途別の初期セクション構成
 *      USE_CASE_LABEL — 用途名表示
 */
import type {
  DocumentSection,
  DocumentSectionType,
  DocumentUseCase,
} from '@goenhub/shared';

import { caseStudiesDefinition } from './caseStudies';
import { comparisonDefinition } from './comparison';
import { ctaDefinition } from './cta';
import { faqDefinition } from './faq';
import { featureGridDefinition } from './featureGrid';
import { heroDefinition } from './hero';
import { imageDefinition } from './image';
import { leaseTermsDefinition } from './leaseTerms';
import { pricingPlansDefinition } from './pricingPlans';
import { processStepsDefinition } from './processSteps';
import { specificationsDefinition } from './specifications';
import { testimonialsDefinition } from './testimonials';
import { textDefinition } from './text';
import type { SectionDefinition, SectionRegistry } from './types';

export const SECTION_REGISTRY: SectionRegistry = {
  hero: heroDefinition,
  text: textDefinition,
  image: imageDefinition,
  cta: ctaDefinition,
  comparison_table: comparisonDefinition,
  feature_grid: featureGridDefinition,
  specifications: specificationsDefinition,
  pricing_plans: pricingPlansDefinition,
  process_steps: processStepsDefinition,
  testimonials: testimonialsDefinition,
  case_studies: caseStudiesDefinition,
  faq: faqDefinition,
  lease_terms: leaseTermsDefinition,
};

/** 推奨表示順序 (AddSectionDialog で並べる順番) */
export const SECTION_TYPE_ORDER: DocumentSectionType[] = [
  'hero',
  'text',
  'image',
  'feature_grid',
  'specifications',
  'comparison_table',
  'pricing_plans',
  'lease_terms',
  'process_steps',
  'testimonials',
  'case_studies',
  'faq',
  'cta',
];

export function getSection(type: DocumentSectionType): SectionDefinition | undefined {
  return SECTION_REGISTRY[type];
}

export function getVariant(type: DocumentSectionType, variant?: string) {
  const def = SECTION_REGISTRY[type];
  if (!def) return undefined;
  const key = variant && def.variants[variant] ? variant : def.defaultVariant;
  return { def, key, variant: def.variants[key] };
}

/** バリアント切替時の初期 config (前回 config を捨てて variant の sample を再投入) */
export function variantSample(type: DocumentSectionType, variant: string) {
  const def = SECTION_REGISTRY[type];
  return def?.variants[variant]?.sample() ?? {};
}

/** 「セクションを追加」時に用途フィルタで利用するヘルパ */
export function isSectionForUseCase(
  def: SectionDefinition,
  useCase: DocumentUseCase | 'all',
): boolean {
  if (useCase === 'all') return true;
  return def.useCases.includes('all') || def.useCases.includes(useCase);
}

export const USE_CASE_LABEL: Record<DocumentUseCase, string> = {
  physical_product: '物理製品',
  service: '無形サービス',
  b2b_lease: 'B2B リース・レンタル',
  platform: 'プラットフォーム',
};

export const USE_CASE_DESCRIPTION: Record<DocumentUseCase, string> = {
  physical_product:
    'ハードウェア・機器・物品など、形ある製品の資料に最適。',
  service: 'コンサルティング・サブスクなど、無形のサービス向け。',
  b2b_lease: 'リース・レンタルなど、期間契約型のビジネス向け。',
  platform: 'SaaS・API・統合プラットフォームなどの資料向け。',
};

interface TemplateSection {
  type: DocumentSectionType;
  variant: string;
}

export const USE_CASE_TEMPLATES: Record<DocumentUseCase, TemplateSection[]> = {
  physical_product: [
    { type: 'hero', variant: 'split' },
    { type: 'image', variant: 'gallery' },
    { type: 'specifications', variant: 'grouped' },
    { type: 'comparison_table', variant: 'spec_sheet' },
    { type: 'faq', variant: 'accordion' },
    { type: 'cta', variant: 'card' },
  ],
  service: [
    { type: 'hero', variant: 'minimal' },
    { type: 'feature_grid', variant: 'icons_3col' },
    { type: 'process_steps', variant: 'horizontal' },
    { type: 'testimonials', variant: 'quote_card' },
    { type: 'pricing_plans', variant: 'tiered_3col' },
    { type: 'cta', variant: 'banner' },
  ],
  b2b_lease: [
    { type: 'hero', variant: 'banner' },
    { type: 'feature_grid', variant: 'icons_3col' },
    { type: 'lease_terms', variant: 'tiered_plans' },
    { type: 'case_studies', variant: 'card_with_metrics' },
    { type: 'faq', variant: 'accordion' },
    { type: 'cta', variant: 'card' },
  ],
  platform: [
    { type: 'hero', variant: 'classic' },
    { type: 'feature_grid', variant: 'icons_4col' },
    { type: 'comparison_table', variant: 'matrix' },
    { type: 'pricing_plans', variant: 'tiered_3col' },
    { type: 'testimonials', variant: 'multi_grid' },
    { type: 'cta', variant: 'banner' },
  ],
};

/**
 * 用途テンプレートから DocumentSection[] を組み立てる (新規作成時)。
 */
export function buildTemplateSections(useCase: DocumentUseCase): DocumentSection[] {
  return USE_CASE_TEMPLATES[useCase].map((t, i) => ({
    id: `s_${Date.now().toString(36)}_${i}_${Math.random().toString(36).slice(2, 6)}`,
    type: t.type,
    variant: t.variant,
    config: variantSample(t.type, t.variant),
  }));
}

export type { SectionDefinition, SectionVariant } from './types';
